import './style.scss'
import { useCallback, useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { IoIosArrowBack } from "react-icons/io"
import VisibilityContext from 'src/provider/state-manager/visibilityProvider'
import AppInfoContext from 'src/provider/state-manager/appInfoProvider'
import ApiContext from 'src/provider/API/call-service'
import { AppLink, AppSpan, AppText, Button, CustomContainer, FlexRow, PageTitle, PageWrapper } from "src/style"
import utility from "src/utils/utility"
import { ProductInfoTab, ProductServiceAccordion, ServerSideTable } from "src/component"
import { productServicesColumns, productBillingSetupColumns, productServicesData, productBillingSetupData } from "src/utils/table-layout"
import ProductImage from 'src/assets/img/product-image.svg'
import QoreidIcon from 'src/assets/img/qoreid-icon.svg'
import { Assign_ModifyFee, EditProduct } from 'src/popup'
import { Product } from 'src/model'
import EditIcon from 'src/assets/svg/product-edit-icon'

const ProductDetails = () => {
    const navigate = useNavigate()
    const { productId } = useParams()
    const {API} = useContext(ApiContext)
    const {info: {selectedProduct}} = useContext(AppInfoContext)
    const {decisionBox} = useContext(VisibilityContext)
    const [recordsInfo, setRecordsInfo] = useState<any>(null)
    const [records, setRecords] = useState<any>(null)
    const [expandedRowId, setExpandedRowId] = useState(null)
    const [productImage, setProductImage] = useState(ProductImage)
    const [selectedRecord, setSelectedRecord] = useState(null)
    const [isBillingSetup, setIsBillingSetup] = useState(false)
    const [product, setProduct] = useState<Product|null>(selectedProduct)
    const [view, setView] = useState('')

    useEffect(() => {
        if (product) handleFetchData()
    }, [isBillingSetup, product])

    useEffect(() => {
        if (selectedProduct?.id) {
            setProduct(selectedProduct)
            setProductImage(selectedProduct?.screenshotUrls?.[0])
        }
    }, [selectedProduct])

    useEffect(() => {
        API.getProductById(productId)
    }, [])

    async function handleFetchData (pageNumber = 1, pageSize = 10) {
        const response = !isBillingSetup ? 
            await API.getServices({
                pageNumber, pageSize, 
                filter: {serviceCategoryId: product?.serviceCategoryId}
            })
            : {data: productBillingSetupData, totalRecords: 4}

            setRecordsInfo(response)
            setRecords(response?.data)
    }

    function handleRowClick (row: any) {
        if (expandedRowId === row.id) setExpandedRowId(null)
        else setExpandedRowId(row.id)
    }

    function handleRowAction (row: any, actionType: string) {
        if (['modify', 'assign'].includes(actionType)) {
            setSelectedRecord(row)
            setView(actionType)
        }
        else {
            decisionBox.show(
                `Are you sure you want to ${actionType} this fee configuration?`,
                null,
                decisionBox.hide,
                `Yes, ${utility.toTitleCase(actionType)}`,
                'No'
            )
        } 
    }

    return (
        <>
            <PageWrapper>
                <FlexRow justifyContent="space-between" gap="2.5" wrap='wrap'>
                    <FlexRow 
                        width="auto" sizeUnit="" gap="1.5"
                        onClick={() => navigate(-1)}
                    >
                        <AppSpan textSize='2.5' cursor='pointer' bottomMargin='1' >
                            <IoIosArrowBack />
                        </AppSpan>
                        <FlexRow gap="1" width="auto" sizeUnit="">
                            <PageTitle 
                                color='#101828' textSize='3' fontWeight="500"
                            >
                                {product?.name}
                            </PageTitle>
                            <AppLink
                                color="#3374EF" textSize="1.6"
                                fontWeight="500" hoverColor="#ADB5BD"
                                href='qoreid.com' target='_blank'
                            >
                                {product?.serviceProvider?.websiteURL || product?.websiteUrl}
                            </AppLink>
                        </FlexRow>
                    </FlexRow>
                    <FlexRow width='auto' sizeUnit='' gap='1.5' wrap='wrap'>
                        <Button
                            width='20'
                            titleSize='1.4' color='#0A756F'
                            height='4' radius='0.8'
                            bgColor='#E7F6F5'
                            hoverBgColor='#E7F6F5' borderColor='#E7F6F5'
                            onClick={() => setView('edit-product')}
                        >
                            <EditIcon style={{marginRight: '1rem'}} />
                            Edit Product Details
                        </Button>
                        <Button
                            width='14.5' 
                            height='4' radius='0.8' 
                            hoverColor='#0D968F'
                            hoverBgColor='#fff' 
                            borderColor='#0D968F'
                            onClick={() => navigate(`/admin/administration/product/${product?.id}/api-calls`)}
                        >
                            View API Calls
                        </Button>
                    </FlexRow>
                </FlexRow>
                
                <CustomContainer topMargin='2.5'>
                    <FlexRow justifyContent='center' gap='2' >
                        <CustomContainer 
                            width="4.8" height="6.5"
                            className='fixed-flex-shape'
                        >
                            <img 
                                src={product?.logoUrl || QoreidIcon} 
                                className='img-covered' 
                                style={{objectFit: 'contain'}}  
                            />
                        </CustomContainer>
                        <AppSpan textSize='3.5' fontWeight="600" color='#409089'>₦{product?.setupFee}</AppSpan>
                    </FlexRow>
                    <CustomContainer 
                        //width="96.8" 
                        height="27" // 22.8
                        maxWidth='96.8' //mxWUnit='%'
                        radius='1.1' topMargin='1.5'
                        className='product-image-container'
                    >
                        <img src={productImage} className='img-covered' />
                    </CustomContainer>
                    <CustomContainer
                        className='scrollable-container'
                    >
                        <FlexRow 
                            justifyContent='center' 
                            gap='4' topMargin='4'
                            style={{minWidth: 'fit-content'}}
                        >
                            { product?.screenshotUrls?.length! > 1 &&
                                product?.screenshotUrls?.map((img, index) => {
                                    return (
                                        <CustomContainer 
                                            width="21" height="10.1" 
                                            radius='0.8' borderColor='#E9EAEC'
                                            key={index}
                                            onClick={() => setProductImage(img)}
                                        >
                                            <img src={img} className='img-covered' />
                                        </CustomContainer>
                                    )
                                })
                            }
                        </FlexRow>
                    </CustomContainer>
                </CustomContainer>
                <CustomContainer topMargin='2.5'>
                    <ProductInfoTab 
                        isAdmin 
                        product={product}
                        isBillingSetup={(val: boolean) => setIsBillingSetup(val)}
                    />
                </CustomContainer>
                <CustomContainer topMargin='2.5'>
                    <CustomContainer
                        style={{display: isBillingSetup ? 'none' : 'block'}}
                    >
                       <AppText 
                            textSize="2.5" color='#101828' 
                            fontWeight="500"
                        >
                            Services
                        </AppText>
                        <ServerSideTable
                            tableData={records}
                            tableColumns={productServicesColumns(handleRowAction)} 
                            dataFetchMethod={handleFetchData}
                            totalRows={recordsInfo?.totalRecords}
                            onExpandableRowClickMethod={handleRowClick}
                            rowId={expandedRowId}
                            accordionComponent={useCallback((data: any) => ProductServiceAccordion({...data, expandedRowId, isBillingSetup, product}), [expandedRowId])}
                            customStyle
                        />
                        {/* <ServerSideTable
                            tableData={records}
                            tableColumns={!isBillingSetup ? productServicesColumns(handleRowAction) : productBillingSetupColumns(handleRowAction)} 
                            dataFetchMethod={handleFetchData}
                            totalRows={recordsInfo?.totalRecords}
                            onExpandableRowClickMethod={handleRowClick}
                            rowId={expandedRowId}
                            accordionComponent={useCallback((data: any) => ProductServiceAccordion({...data, expandedRowId, isBillingSetup, product}), [expandedRowId])}
                            customStyle
                        /> */}
                    </CustomContainer>
                </CustomContainer>
            </PageWrapper>
            { ['assign', 'modify'].includes(view) &&
                <Assign_ModifyFee 
                    serviceData={selectedRecord}
                    productData={product}
                    getServices={handleFetchData}
                    isBillingSetup={isBillingSetup}
                    isEdit={view === 'modify'}
                    close={() => setView('')}
                />
            }
            { view === 'edit-product' &&
                <EditProduct
                    data={product} 
                    close={() => setView('')}
                />
            }
        </>
    )
}

export default utility.routeData('administration/product/:productId', 'Product Details', <ProductDetails/>)
